import React, { useEffect, useState } from "react";
import {
  Table, Tag, Button, Input, Modal, message, Dropdown, Space,
  Tooltip, Form, Select, Row, Col, Drawer, Avatar, Divider, Upload, AutoComplete, Pagination
} from "antd";
import {
  SearchOutlined, EyeOutlined, DeleteOutlined, UserOutlined,
  PhoneOutlined, MailOutlined, EditOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined,
  UserAddOutlined, CrownOutlined, WalletOutlined, CalendarOutlined,
  EnvironmentOutlined, RiseOutlined, TeamOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ManOutlined, WomanOutlined, LockOutlined,
  CameraOutlined, HeartFilled, GiftOutlined, IdcardOutlined,
  WhatsAppOutlined, CopyOutlined, ShareAltOutlined, LinkOutlined,
  SafetyOutlined, EyeInvisibleOutlined,
} from "@ant-design/icons";
import {
  XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, AreaChart, Area,
} from "recharts";
import {
  Hindu, Muslim, MuslimShia, MuslimSunni,
  Christian, Sikh, JainAll, JainDigambar, JainShwetambar,
  Parsi, Buddhist, Jewish, InterReligion, NoReligiousBelief
} from 'indian-religion-caste';
import {
  UserGet, UserRemove, UserPost, UserUpdate, UserTransactionsGet,
  UserDataPostwt,
  UserDataPutwt,
  User_StateGet, User_CasteGet,
  getDistrictsByState, getCitiesByDistrict,
  getCastesByReligion, UserDataGet,
  getPremiumMembers, getReligionOptions,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, formatCurrency, generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./User_Tables.css";

const { Option } = Select;

const occupationOptions = [
  "Government Employee", "Private Employee", "Business",
  "Self-Employed", "Farmer", "Teacher", "Doctor", "Engineer",
  "Lawyer", "Architect", "Banker", "CA", "Professor",
  "Retired", "Housewife", "Other"
];
const fatherOccupationOptions = occupationOptions.filter(
  option => option !== "Housewife"
);
const subCourseData = {
  "B.Sc": ["Physics", "Chemistry", "Mathematics", "Biology"],
  "M.Sc": ["Physics", "Chemistry", "Mathematics"],
  "B.Tech": ["Computer Science", "Electronics", "Mechanical"],
  "MBBS": ["General Medicine", "Pediatrics"],
  "B.Com": ["Accountancy", "Finance"],
  "MBA": ["Marketing", "Finance", "HR"],
  "BCA": ["Web Development", "App Development"],
  "MCA": ["Data Science", "AI & ML"],
  "10th": ["Science", "Commerce", "Arts"],
  "12th": ["Science", "Commerce", "Arts"],
  "Other": ["Other"]
};

const generateHeightOptions = () => {
  const heights = [];
  for (let feet = 4; feet <= 8; feet++) {
    const maxInches = feet === 8 ? 0 : 11;
    for (let inches = 0; inches <= maxInches; inches++) {
      const totalCm = Math.round((feet * 30.48) + (inches * 2.54));
      heights.push({
        label: `${feet}'${inches}" (${totalCm} cm)`,
        value: `${feet}'${inches}" (${totalCm} cm)`,
        feet, inches, cm: totalCm,
      });
    }
  }
  return heights;
};

const HEIGHT_OPTIONS = generateHeightOptions();
const heightOptions = HEIGHT_OPTIONS.map(h => ({ value: h.value, label: h.label }));

const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const motherTongueOptions = ["Hindi", "English", "Marathi", "Punjabi", "Gujarati", "Bengali", "Tamil", "Telugu", "Kannada", "Malayalam", "Odia", "Urdu"];
const courseOptions = ["10th", "12th", "B.Sc", "M.Sc", "B.Tech", "MBBS", "B.Com", "M.Com", "MBA", "BCA", "MCA", "BA", "MA", "B.Ed", "LLB", "Ph.D", "Diploma", "ITI", "Other"];

function User_Tables() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [staffFilter, setStaffFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [staffList, setStaffList] = useState({});
  const [religionOptions, setReligionOptions] = useState([]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const [addLoading, setAddLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCastes, setFilteredCastes] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [subCourses, setSubCourses] = useState([]);
  const [isOtherCourse, setIsOtherCourse] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // 📱 Mobile Responsiveness States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileStats, setShowMobileStats] = useState(false);

  const adminName = localStorage.getItem("username") || "Admin";
  const adminId = localStorage.getItem("user_id") || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const methodConfig = {
    direct: { icon: '👤', label: 'Direct', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
    referral_code: { icon: '🎁', label: 'Code', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
    referral_link: { icon: '🔗', label: 'Link', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
    self: { icon: '🌐', label: 'Self', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  };

  // Screen Resize Listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // HELPERS
  const detectAddMethod = (user) => {
    if (user.added_method && user.added_method !== 'self') return user.added_method;
    if (user.added_by_staff) {
      if (user.refer_by) return 'referral_code';
      return 'direct';
    }
    if (user.refer_by) return 'referral_code';
    return 'self';
  };

  const detectAddedByStaff = (user, staffMap) => {
    if (user.added_by_staff && staffMap[user.added_by_staff]) return user.added_by_staff;
    if (user.refer_by) {
      const referCodeStr = String(user.refer_by).trim();
      const referCodeNum = parseInt(referCodeStr, 10);
      for (const [staffId, staffData] of Object.entries(staffMap)) {
        const staffReferStr = String(staffData.refer || '').trim();
        const staffReferNum = parseInt(staffReferStr, 10);
        const staffUsername = String(staffData.username || '').trim().toLowerCase();
        if (
          staffReferStr === referCodeStr ||
          (!isNaN(staffReferNum) && !isNaN(referCodeNum) && staffReferNum === referCodeNum) ||
          staffUsername === referCodeStr.toLowerCase()
        ) return staffId;
      }
    }
    return null;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersRes, txRes, profileRes, premiumRes] = await Promise.all([
        UserGet(),
        UserTransactionsGet(),
        UserDataGet(),
        getPremiumMembers(),
      ]);

      const allUsers = Array.isArray(usersRes) ? usersRes : [];
      const allProfiles = Array.isArray(profileRes) ? profileRes : [];
      const allPremiums = Array.isArray(premiumRes) ? premiumRes : [];
      const allTransactions = Array.isArray(txRes) ? txRes : [];
      const staffUsers = allUsers.filter(u => u.role === "staff");

      const staffMap = {};
      staffUsers.forEach(s => {
        staffMap[s.id] = {
          name: s.first_name || s.username || `Staff-${s.id}`,
          refer: String(s.refer || ''),
          username: s.username,
        };
      });
      setStaffList(staffMap);

      const usersOnly = allUsers
        .filter(i => i.role === "user")
        .map(u => {
          const profile = allProfiles.find(p =>
            String(p.user_id) === String(u.id) ||
            String(p.user?.id) === String(u.id) ||
            String(p.username) === String(u.username)
          );

          const premium = allPremiums.find(p =>
            String(p.user_id) === String(u.id) ||
            String(p.id) === String(u.id)
          );

          const userTransactions = allTransactions.filter(tx =>
            (String(tx.user_id) === String(u.id) || String(tx.user) === String(u.id)) &&
            (tx.status === 'success' || tx.status === 'approved') &&
            tx.type === 'credit'
          );

          const totalSpent = userTransactions.reduce(
            (sum, tx) => sum + (parseFloat(tx.amount) || 0), 0
          );

          const lastTransaction = userTransactions.length > 0
            ? userTransactions.sort((a, b) =>
              new Date(b.created_date || b.date) - new Date(a.created_date || a.date)
            )[0]
            : null;

          return {
            ...u,
            ...(profile || {}),
            id: u.id,
            username: u.username,
            is_premium: !!(premium || u.is_premium || profile?.premium),
            premium: !!(premium || u.is_premium || profile?.premium),
            plan_name: premium?.plan_name || u.plan_name || null,
            plan_amount: premium?.plan_amount || u.plan_amount || 0,
            plan_expiry: premium?.plan_expiry || u.plan_expiry || null,
            plan_start_date: premium?.plan_start_date || u.plan_start_date || null,
            plan_type: premium?.plan_type || null,
            membership_status: premium?.membership_status || null,
            payment_mode: premium?.payment_mode || lastTransaction?.payment_mode || null,
            granted_by_name: premium?.granted_by_name || null,
            total_spent: totalSpent,
            last_payment_date: lastTransaction?.created_date || lastTransaction?.date || null,
            transaction_count: userTransactions.length,
            _detected_method: detectAddMethod(u),
            _detected_staff: detectAddedByStaff(u, staffMap),
          };
        });

      setUsers(usersOnly);
      setFilteredUsers(usersOnly);
      setMonthlyPayments(calculateMonthlyPayments(allTransactions));
    } catch (error) {
      console.error("❌ Fetch error:", error);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyPayments = (txData) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthlyTotals = {};
    months.forEach(m => (monthlyTotals[m] = 0));
    txData.forEach(tx => {
      if (tx.status === "success" || tx.status === "approved") {
        const date = new Date(tx.created_date || tx.date);
        if (!isNaN(date)) {
          const monthName = date.toLocaleString("en-US", { month: "short" });
          monthlyTotals[monthName] = (monthlyTotals[monthName] || 0) + (parseFloat(tx.amount) || 0);
        }
      }
    });
    return months.map(m => ({ month: m, payment: monthlyTotals[m] || 0 }));
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    let filtered = [...users];
    if (searchText) {
      filtered = filtered.filter(user =>
        user.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.contact?.includes(searchText) ||
        (user.profile_id || user.user?.profile_id || generateRefNumber("user", user.id))
          .toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (statusFilter !== "all") {
      filtered = filtered.filter(u => {
        const isActive = u.status === "active" || u.is_active;
        if (statusFilter === "active") return isActive;
        if (statusFilter === "inactive") return !isActive;
        if (statusFilter === "premium") return u.is_premium || u.premium;
        return true;
      });
    }
    if (methodFilter !== "all") {
      filtered = filtered.filter(u => u._detected_method === methodFilter);
    }
    if (staffFilter !== "all") {
      if (staffFilter === "self") {
        filtered = filtered.filter(u => u._detected_method === "self");
      } else {
        filtered = filtered.filter(u => String(u._detected_staff) === String(staffFilter));
      }
    }
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchText, statusFilter, staffFilter, methodFilter, users]);

  useEffect(() => {
    User_StateGet()
      .then(res => setStates(res || []))
      .catch(() => setStates([]));
    getReligionOptions()
      .then(res => setReligionOptions(res.religions || []))
      .catch(() => setReligionOptions([]));
  }, []);

  const getReligionIcon = (religion) => {
    const icons = {
      'Hindu': '🕉️', 'Muslim': '🕌', 'MuslimShia': '🕌', 'MuslimSunni': '🕌',
      'Christian': '⛪', 'Sikh': '🪯', 'Jain': '✋', 'JainDigambar': '✋',
      'JainShwetambar': '✋', 'Buddhist': '☸️', 'Parsi': '🔥', 'Jewish': '✡️',
      'InterReligion': '🤝', 'NoReligiousBelief': '🌿'
    };
    return icons[religion] || '🔯';
  };

  const handleStateChange = async (value) => {
    setDistricts([]); setCities([]);
    form.setFieldsValue({ district: undefined, city: undefined });
    if (!value) return;
    try {
      const res = await getDistrictsByState(value);
      setDistricts(res?.districts || (Array.isArray(res) ? res : []));
    } catch { setDistricts([]); }
  };

  const handleDistrictChange = async (value) => {
    setCities([]);
    form.setFieldsValue({ city: undefined });
    if (!value) return;
    try {
      const res = await getCitiesByDistrict(value);
      setCities(res?.cities || (Array.isArray(res) ? res : []));
    } catch { setCities([]); }
  };

  const handleReligionChange = (value) => {
    setFilteredCastes([]);
    form.setFieldsValue({ caste: undefined, subcaste: undefined });
    if (!value) return;
    try {
      let castes = [];
      switch (value) {
        case 'Hindu': castes = Hindu; break;
        case 'Muslim':
        case 'MuslimShia': castes = MuslimShia; break;
        case 'MuslimSunni': castes = MuslimSunni; break;
        case 'Christian': castes = Christian; break;
        case 'Sikh': castes = Sikh; break;
        case 'Jain':
        case 'JainDigambar': castes = JainDigambar; break;
        case 'JainShwetambar': castes = JainShwetambar; break;
        case 'Buddhist': castes = Buddhist; break;
        case 'Parsi': castes = Parsi; break;
        case 'Jewish': castes = Jewish; break;
        case 'InterReligion': castes = InterReligion; break;
        case 'NoReligiousBelief': castes = NoReligiousBelief; break;
        default: castes = [];
      }
      setFilteredCastes(castes);
    } catch { setFilteredCastes([]); }
  };

  const handleCourseChange = (value) => {
    setSelectedCourse(value);
    form.setFieldsValue({ sub_course: undefined });
    if (value === 'Other') { setIsOtherCourse(true); setSubCourses([]); return; }
    setIsOtherCourse(false);
    setSubCourses(subCourseData[value] || []);
  };

  const handleAddUser = async (values) => {
    try {
      setAddLoading(true);
      const cleanData = {
        username: values.username.trim(),
        password: values.password,
        email: values.email || "",
        first_name: values.firstname || "",
        contact: values.contact || "",
        gender: values.gender || "",
        state: values.state || "",
        district: values.district || "",
        role: "user",
        added_by_type: "admin",
        added_method: "direct",
      };

      try {
        const userData = await UserPost(cleanData);
        if (!userData || !userData.user_id) {
          message.success("✅ User created!");
          handleModalClose();
          fetchUsers();
          return;
        }

        const newUserId = userData.user_id;

        let age = null;
        if (values.dob) {
          const d = new Date(values.dob);
          const t = new Date();
          age = t.getFullYear() - d.getFullYear();
          const m = t.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
        }

        try {
          const profileData = new FormData();
          profileData.append("user_id", newUserId);
          profileData.append("username", values.username.trim());
          profileData.append("role", "user");

          const textFields = [
            "firstname", "gender", "state", "city", "caste", "subcaste", "religion",
            "email", "contact", "contact1", "dob", "father_name", "mother_name",
            "father_occupation", "mother_occupation", "university", "course",
            "sub_course", "job_title", "job_type", "Lifestyle", "marrige_status",
            "mother_tongue", "height", "blood_group", "created_profile",
            "food_lifestyle", "additional_notes",
          ];

          textFields.forEach(key => {
            const val = values[key];
            if (val !== undefined && val !== null && val !== "") {
              profileData.append(key, val);
            }
          });

          if (values.district) profileData.append("district", values.district);
          profileData.append("country", values.country || "India");
          if (age !== null) profileData.append("age", String(age));

          ["brother", "brother_marrige", "sister", "sister_marrige"].forEach(key => {
            profileData.append(
              key,
              values[key] !== undefined && values[key] !== null ? String(values[key]) : "0"
            );
          });

          if (values.annual_income) profileData.append("annual_income", String(values.annual_income));
          profileData.append("added_by_type", "admin");
          profileData.append("added_method", "direct");
          if (file) profileData.append("pic", file);

          await UserDataPostwt(profileData);

        } catch (profileError) {
          console.warn("⚠️ Profile save failed:", profileError);
          message.warning("User created but profile save failed. Edit later.");
          handleModalClose();
          fetchUsers();
          return;
        }

        message.success("✅ User added successfully!");
        handleModalClose();
        fetchUsers();

      } catch (error) {
        const errorMsg =
          error?.response?.data?.username?.[0] ||
          error?.response?.data?.email?.[0] ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message || "Failed to add user";
        if (errorMsg.includes("username") || errorMsg.includes("already")) {
          message.error("Username already exists!");
        } else {
          message.error(errorMsg);
        }
      }
    } catch (error) {
      message.error(error?.message || "Failed to add user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    form.resetFields();
    setFile(null);
    setPreviewUrl(null);
    setDistricts([]); setCities([]); setFilteredCastes([]);
    setSubCourses([]); setSelectedCourse(''); setIsOtherCourse(false);
  };

  const handleEdit = async (user) => {
    let freshUser = user;

    try {
      const [usersRes, profileRes] = await Promise.all([UserGet(), UserDataGet()]);
      const allUsers = Array.isArray(usersRes) ? usersRes : [];
      const allProfiles = Array.isArray(profileRes) ? profileRes : [];

      const foundUser = allUsers.find(u => u.id === user.id);
      const foundProfile = allProfiles.find(p =>
        String(p.user_id) === String(user.id) ||
        String(p.user?.id) === String(user.id) ||
        String(p.username) === String(user.username)
      );

      if (foundUser) {
        freshUser = {
          ...foundUser,
          ...(foundProfile || {}),
          id: foundUser.id,
          username: foundUser.username,
        };
      }
    } catch (error) {
      console.error("Failed to fetch fresh data:", error);
    }

    setSelectedUser(freshUser);

    if (freshUser.pic) {
      setPreviewUrl(typeof freshUser.pic === 'string' ? freshUser.pic : null);
    }

    if (freshUser.state) {
      try {
        const res = await getDistrictsByState(freshUser.state);
        setDistricts(res?.districts || (Array.isArray(res) ? res : []));
      } catch { setDistricts([]); }
    }

    if (freshUser.district) {
      try {
        const res = await getCitiesByDistrict(freshUser.district);
        setCities(res?.cities || (Array.isArray(res) ? res : []));
      } catch { setCities([]); }
    }

    if (freshUser.religion) {
      let castes = [];
      switch (freshUser.religion) {
        case 'Hindu': castes = Hindu; break;
        case 'Muslim':
        case 'MuslimShia': castes = MuslimShia; break;
        case 'MuslimSunni': castes = MuslimSunni; break;
        case 'Christian': castes = Christian; break;
        case 'Sikh': castes = Sikh; break;
        case 'Jain':
        case 'JainDigambar': castes = JainDigambar; break;
        case 'JainShwetambar': castes = JainShwetambar; break;
        case 'Buddhist': castes = Buddhist; break;
        case 'Parsi': castes = Parsi; break;
        case 'Jewish': castes = Jewish; break;
        default: castes = [];
      }
      setFilteredCastes(castes);
    }

    if (freshUser.course && subCourseData[freshUser.course]) {
      setSubCourses(subCourseData[freshUser.course]);
    }

    editForm.setFieldsValue({
      username: freshUser.username || '',
      email: freshUser.email || '',
      contact: freshUser.contact || '',
      contact1: freshUser.contact1 || '',
      firstname: freshUser.first_name || freshUser.firstname || '',
      gender: freshUser.gender || '',
      dob: freshUser.dob || '',
      marrige_status: freshUser.marrige_status || 'Unmarried',
      height: freshUser.height || '',
      blood_group: freshUser.blood_group || '',
      mother_tongue: freshUser.mother_tongue || '',
      food_lifestyle: freshUser.food_lifestyle || '',
      state: freshUser.state || '',
      district: freshUser.district || '',
      city: freshUser.city || '',
      country: freshUser.country || 'India',
      religion: freshUser.religion || '',
      caste: freshUser.caste || '',
      subcaste: freshUser.subcaste || '',
      father_name: freshUser.father_name || '',
      mother_name: freshUser.mother_name || '',
      father_occupation: freshUser.father_occupation || '',
      mother_occupation: freshUser.mother_occupation || '',
      brother: freshUser.brother || 0,
      brother_marrige: freshUser.brother_marrige || 0,
      sister: freshUser.sister || 0,
      sister_marrige: freshUser.sister_marrige || 0,
      university: freshUser.university || '',
      course: freshUser.course || '',
      sub_course: freshUser.sub_course || '',
      job_title: freshUser.job_title || '',
      job_type: freshUser.job_type || '',
      annual_income: freshUser.annual_income || '',
      Lifestyle: freshUser.Lifestyle || '',
      created_profile: freshUser.created_profile || '',
      additional_notes: freshUser.additional_notes || '',
      new_password: '',
      confirm_password: '',
    });

    setEditModalOpen(true);
  };

  const handleUpdateUser = async (values) => {
    try {
      setAddLoading(true);

      if (showPasswordChange) {
        if (!values.new_password) {
          message.error("Please enter new password!");
          setAddLoading(false);
          return;
        }
        if (values.new_password !== values.confirm_password) {
          message.error("Passwords do not match!");
          setAddLoading(false);
          return;
        }
      }

      let age = null;
      if (values.dob) {
        const d = new Date(values.dob);
        const t = new Date();
        age = t.getFullYear() - d.getFullYear();
        const m = t.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
      }

      const updatePayload = {
        email: values.email,
        first_name: values.firstname,
        contact: values.contact,
        gender: values.gender,
      };

      if (showPasswordChange && values.new_password) {
        updatePayload.password = values.new_password;
      }

      const profileData = new FormData();
      profileData.append("user_id", selectedUser.id);
      profileData.append("username", values.username || selectedUser.username);
      profileData.append("role", "user");

      const textFields = [
        "firstname", "gender", "state", "city", "caste", "subcaste", "religion",
        "email", "contact", "contact1", "dob", "father_name", "mother_name",
        "father_occupation", "mother_occupation", "university", "course",
        "sub_course", "job_title", "job_type", "Lifestyle", "marrige_status",
        "mother_tongue", "height", "blood_group", "created_profile",
        "food_lifestyle", "additional_notes",
      ];

      textFields.forEach(key => {
        const val = values[key];
        if (val !== undefined && val !== null && val !== "") {
          profileData.append(key, val);
        }
      });

      if (values.district) profileData.append("district", values.district);
      profileData.append("country", values.country || "India");
      if (age !== null) profileData.append("age", String(age));

      ["brother", "brother_marrige", "sister", "sister_marrige"].forEach(key => {
        profileData.append(
          key,
          values[key] !== undefined && values[key] !== null ? String(values[key]) : "0"
        );
      });

      if (values.annual_income) profileData.append("annual_income", String(values.annual_income));
      if (file) profileData.append("pic", file);

      await UserUpdate(selectedUser.id, updatePayload);
      await UserDataPutwt(selectedUser.id, profileData);

      message.success(
        showPasswordChange && values.new_password
          ? "✅ User updated & password changed successfully!"
          : "✅ User updated successfully!"
      );

      handleEditModalClose();
      fetchUsers();

    } catch (error) {
      console.error("Update error:", error);
      message.error(
        error?.response?.data?.password?.[0] ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update user"
      );
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    editForm.resetFields();
    setFile(null);
    setPreviewUrl(null);
    setDistricts([]); setCities([]); setFilteredCastes([]);
    setSubCourses([]); setSelectedCourse(''); setIsOtherCourse(false);
    setShowPasswordChange(false);
  };

  const handleDelete = (id) => {
    if (!id) { message.error("Invalid user ID"); return; }
    Modal.confirm({
      title: "Delete User?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await UserRemove(id);
          message.success("User deleted!");
          fetchUsers();
        } catch (error) { }
      },
    });
  };

  const handleCall = (contact) => {
    if (!contact) return message.warning("No contact number available");
    window.location.href = `tel:${contact}`;
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setDetailsDrawerOpen(true);
  };

  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "profile_id", label: "Profile ID", format: r => r.profile_id || generateRefNumber("user", r.id || 0) },
    { key: "name", label: "User Name", format: r => capitalize(r.first_name) || capitalize(r.username) || "N/A" },
    { key: "email", label: "Email", format: r => r.email || "N/A" },
    { key: "contact", label: "Mobile", format: r => r.contact || "N/A" },
    { key: "plan_name", label: "Plan", format: r => r.plan_name || "Free" },
    { key: "payment_mode", label: "Payment Mode", format: r => r.payment_mode || '-' },
    { key: "total_spent", label: "Total Spent", format: r => r.total_spent ? `₹${r.total_spent}` : '₹0' },
    {
      key: "added_by", label: "Added By", format: r => {
        if (r._detected_method === "self") return "Self Register";
        const staff = staffList[r._detected_staff];
        if (staff) return `${staff.name} (via ${r._detected_method})`;
        if (r.refer_by) return `Code: ${r.refer_by} (External)`;
        return "Self Register";
      }
    },
    { key: "created_date", label: "Created", format: r => formatDate(r.created_date || r.date_joined) },
  ];

  const handleExportCSV = (all = false) =>
    exportToCSV(all ? users : filteredUsers, exportColumns, "users_report");
  const handleExportPDF = (all = false) =>
    exportToPDF(all ? users : filteredUsers, exportColumns, {
      title: "Users Report", subtitle: "Complete list", filename: "users_report",
    });

  const stats = {
    total: users.length,
    active: users.filter(u => u.status === "active" || u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    premium: users.filter(u => u.is_premium || u.premium).length,
    male: users.filter(u => u.gender?.toLowerCase() === "male").length,
    female: users.filter(u => u.gender?.toLowerCase() === "female").length,
    selfRegistered: users.filter(u => u._detected_method === "self").length,
    directAdded: users.filter(u => u._detected_method === "direct").length,
    viaCode: users.filter(u => u._detected_method === "referral_code").length,
    viaLink: users.filter(u => u._detected_method === "referral_link").length,
    totalRevenue: users.reduce((sum, u) => sum + parseFloat(u.total_spent || 0), 0),
    paidUsers: users.filter(u => parseFloat(u.total_spent || 0) > 0).length,
    adminGranted: users.filter(u => u.granted_by_name).length,
  };

  const columns = [
    {
      title: "#", key: "sno", width: 45, fixed: "left",
      render: (_, __, i) => (
        <span className="sno-cell">
          {(currentPage - 1) * pageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Profile ID", key: "profile_id", width: 120,
      render: (_, r) => {
        const profileId = r.profile_id || r.user?.profile_id || generateRefNumber("user", r.id);
        return <span className="ref-cell">{profileId}</span>;
      },
    },
    {
      title: "User", key: "name", width: 180,
      render: (_, r) => (
        <div className="user-name-cell">
          <div className="user-avatar">{r.first_name?.[0] || r.username?.[0] || "U"}</div>
          <div className="user-name-info">
            <div className="user-name-text">{capitalize(r.first_name) || r.username || "N/A"}</div>
            <div className="user-email-text">{r.email || "No email"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile", dataIndex: "contact", key: "contact", width: 115,
      render: c => <span className="mobile-cell"><PhoneOutlined /> {c || "N/A"}</span>
    },
    {
      title: "Plan", key: "plan", width: 140,
      render: (_, r) => {
        const isPremium = r.is_premium || r.premium;
        if (!isPremium) return <Tag className="plan-tag free" style={{ fontSize: 11, fontWeight: 600 }}>Free</Tag>;
        const isExpired = r.plan_expiry && new Date(r.plan_expiry) < new Date();
        return (
          <Tag
            className={`plan-tag ${isExpired ? 'inactive' : 'premium'}`}
            style={{
              fontSize: 11, fontWeight: 700,
              background: isExpired ? '#fee2e2' : 'linear-gradient(135deg, #ff0055, #ff4d88)',
              color: isExpired ? '#dc2626' : '#fff',
              border: 'none',
            }}
          >
            {isExpired ? '⏰ Expired' : '⭐ Premium'}
          </Tag>
        );
      }
    },
    {
      title: "Status", key: "status", width: 85,
      render: (_, r) => {
        const isActive = r.status === "active" || r.is_active;
        return <Tag className={`status-tag ${isActive ? "active" : "inactive"}`}>{isActive ? "● Active" : "○ Inactive"}</Tag>;
      },
    },
    {
      title: "Created", key: "created", width: 100,
      render: (_, r) => <span className="date-cell">{formatDate(r.created_date || r.date_joined)}</span>
    },
    {
      title: "Added By", key: "added_by", width: 180,
      render: (_, r) => {
        const method = methodConfig[r._detected_method] || methodConfig.self;
        const staffData = staffList[r._detected_staff];
        if (r._detected_method === "self") {
          return (
            <Tag style={{ background: method.bg, color: method.color, border: `1px solid ${method.border}`, fontSize: 11, fontWeight: 600, margin: 0 }}>
              {method.icon} Self Register
            </Tag>
          );
        }
        if (staffData) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: method.bg, color: method.color, border: `1px solid ${method.border}`, borderRadius: 6, fontSize: 11, fontWeight: 700, maxWidth: 160 }}>
                <span>{method.icon}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{staffData.name}</span>
              </div>
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textAlign: 'center', background: '#f8fafc', padding: '1px 6px', borderRadius: 4 }}>via {method.label}</span>
            </div>
          );
        }
        if (r.refer_by) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 11, fontWeight: 700, maxWidth: 160 }}>
                <span>🎁</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>Code: {r.refer_by}</span>
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textAlign: 'center', background: '#f8fafc', padding: '1px 6px', borderRadius: 4 }}>External Referral</span>
            </div>
          );
        }
        return <Tag style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontSize: 11, fontWeight: 600, margin: 0 }}>🌐 Self Register</Tag>;
      },
    },
    {
      title: "Actions", key: "action", width: 140, fixed: "right",
      render: (_, r) => (
        <Space size={3}>
          <Tooltip title="View">  <Button size="small" icon={<EyeOutlined />} className="action-btn view" onClick={() => handleViewDetails(r)} /></Tooltip>
          <Tooltip title="Edit">  <Button size="small" icon={<EditOutlined />} className="action-btn edit" onClick={() => handleEdit(r)} /></Tooltip>
          <Tooltip title="Call">  <Button size="small" icon={<PhoneOutlined />} className="action-btn call" onClick={() => handleCall(r.contact)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" icon={<DeleteOutlined />} className="action-btn delete" onClick={() => handleDelete(r.id)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredUsers.length})`, onClick: () => handleExportCSV(false) },
      { key: "2", label: `Export All (${users.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredUsers.length})`, onClick: () => handleExportPDF(false) },
      { key: "2", label: `Export All (${users.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  return (
    <div className="user-tables-page">

            {/* HEADER & STATS WRAPPER */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          {/* LEFT SIDE: Title */}
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><TeamOutlined /></div>
              <div>
                <h2 className="page-title">User Management</h2>
                <p className="page-subtitle">{filteredUsers.length} of {users.length} users</p>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: All 5 Buttons in a Single Row */}
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} className="icon-btn" />
            </Tooltip>
            
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn">
                <span>CSV</span>
              </Button>
            </Dropdown>
            
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn">
                <span>PDF</span>
              </Button>
            </Dropdown>
            
            <Button type="primary" icon={<UserAddOutlined />} className="add-btn" onClick={() => setAddModalOpen(true)}>
              <span>Add User</span>
            </Button>

            {/* 📱 Mobile Only: Stats Toggle (Sirf Arrow 🔽) Same Line Mein */}
            {isMobile && (
              <button
                type="button"
                className="mobile-stats-icon-btn"
                onClick={() => setShowMobileStats(prev => !prev)}
              >
                {showMobileStats ? <UpOutlined /> : <DownOutlined />}
              </button>
            )}
          </div>
        </div>

                {/* Animated Smooth Stats Accordion (3 Columns on Mobile, 1 Line on Desktop) */}
        <div className={`mini-stats-bar ${isMobile && showMobileStats ? 'show' : ''}`}>
          
          <div className="mini-stat total">
            <TeamOutlined />
            <div className="stat-content">
              <b>{stats.total}</b> <span>Total</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat active">
            <CheckCircleOutlined />
            <div className="stat-content">
              <b>{stats.active}</b> <span>Active</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat premium">
            <CrownOutlined />
            <div className="stat-content">
              <b>{stats.premium}</b> <span>Premium</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat male">
            <ManOutlined />
            <div className="stat-content">
              <b>{stats.male}</b> <span>Male</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat female">
            <WomanOutlined />
            <div className="stat-content">
              <b>{stats.female}</b> <span>Female</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#16a34a' }}>
            <UserOutlined />
            <div className="stat-content">
              <b>{stats.selfRegistered}</b> <span>Self</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#3b82f6' }}>
            <UserAddOutlined />
            <div className="stat-content">
              <b>{stats.directAdded}</b> <span>Direct</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#8b5cf6' }}>
            <GiftOutlined />
            <div className="stat-content">
              <b>{stats.viaCode}</b> <span>Via Code</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#f59e0b' }}>
            <LinkOutlined />
            <div className="stat-content">
              <b>{stats.viaLink}</b> <span>Via Link</span>
            </div>
          </div>

        </div>
      </div>

      {/* CHART */}
      <div className="chart-card">
        <div className="chart-header">
          <div className="chart-title-block">
            <div className="chart-icon"><RiseOutlined /></div>
            <div>
              <h3>Monthly Payment Trends</h3>
              <p>Payment analysis for current year</p>
            </div>
          </div>
          <Tag className="chart-tag">
            Total: ₹{monthlyPayments.reduce((s, m) => s + m.payment, 0).toLocaleString("en-IN")}
          </Tag>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={monthlyPayments} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorPayment" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0055" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#ff0055" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#888", fontSize: 11 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: "#888", fontSize: 11 }}
              tickFormatter={v => v >= 1000 ? `₹${v / 1000}K` : `₹${v}`} />
            <ChartTooltip
              formatter={v => [`₹${v.toLocaleString("en-IN")}`, "Payment"]}
              contentStyle={{ borderRadius: 8, border: "1px solid #f0f0f0", fontSize: 12 }}
            />
            <Area type="monotone" dataKey="payment" stroke="#ff0055" strokeWidth={2}
              fill="url(#colorPayment)" dot={{ fill: "#ff0055", r: 2.5 }} activeDot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* MAIN TABLE / CARDS SECTION */}
      <div className="table-card">

        {/* UNIFIED FILTERS */}
        <div className="table-filters">
          <Input
            placeholder="Search users by name, profile ID, email or mobile..."
            prefix={<SearchOutlined className="search-icon" />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="search-box"
            allowClear
          />
          <Select className="status-filter" value={statusFilter} onChange={setStatusFilter}>
            <Option value="all">All Users</Option>
            <Option value="active">Active Only</Option>
            <Option value="inactive">Inactive Only</Option>
            <Option value="premium">Premium Only</Option>
          </Select>
          <Select className="status-filter" value={methodFilter} onChange={setMethodFilter}>
            <Option value="all">🎯 All Methods</Option>
            <Option value="self">🌐 Self Register</Option>
            <Option value="direct">👤 Direct Add</Option>
            <Option value="referral_code">🎁 Via Code</Option>
            <Option value="referral_link">🔗 Via Link</Option>
          </Select>
          <Select
            className="status-filter"
            value={staffFilter} onChange={setStaffFilter}
            showSearch
            filterOption={(input, option) => String(option.children).toLowerCase().includes(input.toLowerCase())}
          >
            <Option value="all">👥 All Staff</Option>
            <Option value="self">🌐 Self Registered</Option>
            {Object.entries(staffList).map(([staffId, staffData]) => (
              <Option key={staffId} value={staffId}>👤 {staffData.name}</Option>
            ))}
          </Select>

          {(searchText || statusFilter !== "all" || staffFilter !== "all" || methodFilter !== "all") && (
            <Button onClick={() => { setSearchText(""); setStatusFilter("all"); setStaffFilter("all"); setMethodFilter("all"); }} size="middle" style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              Clear All Filters
            </Button>
          )}
        </div>

        {staffFilter !== "all" && staffFilter !== "self" && filteredUsers.length > 0 && (
          <div style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', borderRadius: 8, margin: '0 0 12px', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#1e40af', fontSize: 13 }}>👤 {staffList[staffFilter]?.name} added:</span>
            <span style={{ fontSize: 12 }}><UserAddOutlined style={{ color: '#3b82f6' }} /> Direct: <b>{users.filter(u => String(u._detected_staff) === String(staffFilter) && u._detected_method === "direct").length}</b></span>
            <span style={{ fontSize: 12 }}><GiftOutlined style={{ color: '#8b5cf6' }} /> Via Code: <b>{users.filter(u => String(u._detected_staff) === String(staffFilter) && u._detected_method === "referral_code").length}</b></span>
            <span style={{ fontSize: 12 }}><LinkOutlined style={{ color: '#f59e0b' }} /> Via Link: <b>{users.filter(u => String(u._detected_staff) === String(staffFilter) && u._detected_method === "referral_link").length}</b></span>
            <span style={{ fontSize: 12, marginLeft: 'auto', fontWeight: 700, color: '#1e40af' }}>Total: {users.filter(u => String(u._detected_staff) === String(staffFilter)).length}</span>
          </div>
        )}

        {/* 📱 MOBILE CARDS VIEW */}
        {isMobile ? (
          <div className="mobile-user-list">
            {filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((r) => {
              const isActive = r.status === "active" || r.is_active;
              const isPremium = r.is_premium || r.premium;
              return (
                <div className="mobile-user-card" key={r.id}>
                  <div className="muc-header">
                    <span className="muc-id">{r.profile_id || generateRefNumber("user", r.id)}</span>
                    <span className={`muc-status ${isActive ? 'active' : 'inactive'}`}>
                      {isActive ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                  <div className="muc-body">
                    <div className="muc-avatar">
                      {r.image || r.pic ? <img src={r.image || r.pic} alt="User" /> : (r.first_name?.[0] || r.username?.[0] || 'U')}
                    </div>
                    <div className="muc-info">
                      <h4 className="muc-name">{capitalize(r.first_name) || r.username}</h4>
                      <span className="muc-phone"><PhoneOutlined /> {r.contact || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="muc-footer">
                    <div className={`muc-plan ${isPremium ? 'premium' : ''}`}>
                      {isPremium ? '⭐ Premium' : 'Free Plan'}
                    </div>
                    <div className="muc-actions">
                      <button className="action-btn view" onClick={() => handleViewDetails(r)}><EyeOutlined /></button>
                      <button className="action-btn edit" onClick={() => handleEdit(r)}><EditOutlined /></button>
                      <button className="action-btn call" onClick={() => handleCall(r.contact)}><PhoneOutlined /></button>
                      <button className="action-btn delete" onClick={() => handleDelete(r.id)}><DeleteOutlined /></button>
                    </div>
                  </div>
                </div>
              );
            })}

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredUsers.length}
              onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
              size="small"
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </div>
        ) : (
          /* 💻 DESKTOP TABLE VIEW */
          <div className="desktop-table-wrapper">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredUsers}
              loading={loading}
              size="small"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                size: "small",
                showTotal: total => `Total ${total}`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }} scroll={{ x: 1200 }}
            />
          </div>
        )}
      </div>

      {/* ADD USER MODAL */}
      <Modal
        title={
          <div className="su-modal-title-new">
            <div className="su-modal-title-icon-wrap"><UserAddOutlined /></div>
            <div className="su-modal-title-text">
              <h3>Add New User</h3>
              <p>Create a complete user profile</p>
            </div>
          </div>
        }
        open={addModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={880}
        destroyOnHidden
        className="su-add-modal"
        centered
      >
        <div className="su-staff-strip">
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon staff"><IdcardOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Admin</div>
              <div className="su-staff-badge-value">{adminName} (ADMIN)</div>
            </div>
          </div>
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon referral"><SafetyOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Access Level</div>
              <div className="su-staff-badge-value">Full Access</div>
            </div>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUser}
          requiredMark={false}
          onFinishFailed={errorInfo => {
            if (errorInfo.errorFields.length > 0) {
              message.error(`Please fix ${errorInfo.errorFields.length} error(s)`);
              form.scrollToField(errorInfo.errorFields[0].name, { behavior: "smooth", block: "center" });
            }
          }}
        >
          <div className="su-photo-section">
            <Upload beforeUpload={f => { setFile(f); setPreviewUrl(URL.createObjectURL(f)); return false; }} showUploadList={false}>
              <div className="su-photo-upload-new">
                {previewUrl
                  ? <img src={previewUrl} alt="preview" className="su-photo-preview-new" />
                  : <div className="su-photo-placeholder"><CameraOutlined /><span>Add Photo</span></div>
                }
              </div>
            </Upload>
            <p className="su-photo-hint-new">📸 Upload a clear photo (optional)</p>
          </div>

          <div className="su-form-body">
            {/* Account */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji account">🔐</div>
                <div><div className="su-section-title">Account Information</div><div className="su-section-subtitle">Login credentials for the user</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="username" label={<span><UserOutlined style={{ marginRight: 6, color: "#e91e63" }} />Username <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Username required" }, { min: 4, max: 16, message: "4-16 characters" }, { pattern: /^[a-z0-9]+$/, message: "Only lowercase & numbers" }]}>
                  <Input placeholder="e.g. rahul2024" />
                </Form.Item>
                <Form.Item name="password" label={<span><LockOutlined style={{ marginRight: 6, color: "#e91e63" }} />Password <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Password required" }, { min: 8, message: "Min 8 characters" }, { pattern: /[A-Z]/, message: "One uppercase needed" }, { pattern: /[0-9]/, message: "One number needed" }]}>
                  <Input.Password placeholder="Strong password" autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="contact" label={<span><PhoneOutlined style={{ marginRight: 6, color: "#e91e63" }} />Mobile <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Mobile required" }, { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }]}
                  normalize={value => value?.replace(/\D/g, '').slice(0, 10)}>
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>
                <Form.Item name="email" label={<span><MailOutlined style={{ marginRight: 6, color: "#e91e63" }} />Email <span className="su-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Email required" },
                    { type: "email", message: "Invalid email format" }
                  ]}>
                  <Input placeholder="user@email.com" />
                </Form.Item>
              </div>
            </div>

            {/* Personal */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji personal">👤</div>
                <div><div className="su-section-title">Personal Details</div><div className="su-section-subtitle">Basic profile information</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="firstname" label={<span>Full Name <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Full Name is required" }]}>
                  <Input placeholder="Enter full name" />
                </Form.Item>
                <Form.Item name="gender" label={<span>Gender <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Gender is required" }]}>
                  <AutoComplete options={["Male", "Female", "Other", "Prefer not to say"].map(v => ({ value: v }))} placeholder="Select or type gender" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="dob" label={<span>Date of Birth <span className="su-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Date of Birth is required" },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const dob = new Date(value); const today = new Date();
                        let age = today.getFullYear() - dob.getFullYear();
                        const m = today.getMonth() - dob.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                        if (dob > today) return Promise.reject("Cannot be future date");
                        if (age < 18) return Promise.reject("Must be 18+ years old");
                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <Input type="date" max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]} />
                </Form.Item>
                <Form.Item name="marrige_status" label={<span>Marital Status <span className="su-required-star">*</span></span>} initialValue="Unmarried"
                  rules={[{ required: true, message: "Marital Status is required" }]}>
                  <AutoComplete options={["Unmarried", "Divorced", "Widowed", "Separated", "Awaited"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="height" label="Height">
                  <AutoComplete options={heightOptions} placeholder="e.g. 5'6&quot; (168 cm)" allowClear
                    filterOption={(input, option) => {
                      const s = input.toLowerCase();
                      return (option?.value || '').toLowerCase().includes(s) || (option?.label || '').toLowerCase().includes(s);
                    }} />
                </Form.Item>
                <Form.Item name="blood_group" label="Blood Group">
                  <AutoComplete options={bloodGroupOptions.map(v => ({ value: v }))} placeholder="e.g. A+, B-, O+" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="mother_tongue" label="Mother Tongue">
                  <AutoComplete options={motherTongueOptions.map(v => ({ value: v }))} placeholder="e.g. Hindi, Gujarati..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="food_lifestyle" label="Food Lifestyle">
                  <AutoComplete options={["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan", "Jain"].map(v => ({ value: v }))} placeholder="e.g. Vegetarian, Vegan..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
            </div>

            {/* Location */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji location">📍</div>
                <div><div className="su-section-title">Location Details</div><div className="su-section-subtitle">Where does the user live?</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="state" label={<span>State <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "State is required" }]}>
                  <AutoComplete options={states.map(s => ({ value: s.state || s }))} placeholder="Type or select state" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => { const m = states.find(s => (s.state || s) === value); if (m) handleStateChange(value); }} />
                </Form.Item>
                <Form.Item name="district" label={<span>District <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "District is required" }]}>
                  <AutoComplete options={districts.map(d => ({ value: d }))} placeholder={districts.length ? "Type or select district" : "Select state first"} allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => { const m = districts.find(d => d === value); if (m) handleDistrictChange(value); }} />
                </Form.Item>
                <Form.Item name="city" label={<span>City / Town <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "City is required" }]}>
                  <AutoComplete options={cities.map(c => ({ value: c }))} placeholder="Enter city name" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="country" label={<span>Country <span className="su-required-star">*</span></span>} initialValue="India"
                  rules={[{ required: true, message: "Country is required" }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="contact1" label="Alternate Mobile"
                  rules={[{ pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }]}
                  normalize={value => value?.replace(/\D/g, '').slice(0, 10)}>
                  <Input placeholder="Optional - 10 digit" maxLength={10} inputMode="numeric" />
                </Form.Item>
              </div>
            </div>

            {/* Religion */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji religion">🕉️</div>
                <div><div className="su-section-title">Religion & Caste</div><div className="su-section-subtitle">Religious background</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="religion" label={<span>Religion <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Religion is required" }]}>
                  <AutoComplete
                    options={religionOptions.map(r => ({ value: r.value || r, label: `${getReligionIcon(r.value || r)} ${r.label || r.value || r}` }))}
                    placeholder="Select religion" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => {
                      const casteReligions = ['Hindu', 'Muslim', 'MuslimShia', 'MuslimSunni', 'Christian', 'Sikh', 'Jain', 'JainDigambar', 'JainShwetambar', 'Buddhist', 'Parsi', 'Jewish', 'InterReligion', 'NoReligiousBelief'];
                      if (casteReligions.includes(value)) { handleReligionChange(value); }
                      else { setFilteredCastes([]); form.setFieldsValue({ caste: undefined }); }
                    }}
                  />
                </Form.Item>
                <Form.Item name="caste" label={<span>Caste <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Caste is required" }]}>
                  <AutoComplete options={filteredCastes.map(c => ({ value: c.name || c }))} placeholder="Select religion first or type caste" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="subcaste" label={<span>Sub Caste <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Sub Caste is required" }]}>
                  <Input placeholder="Type sub caste" />
                </Form.Item>
              </div>
            </div>

            {/* Family */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji family">👨‍👩‍👧‍👦</div>
                <div><div className="su-section-title">Family Details</div><div className="su-section-subtitle">Family background information</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="father_name" label="Father's Name"><Input placeholder="Enter father's name" /></Form.Item>
                <Form.Item name="father_occupation" label="Father's Occupation">
                  <AutoComplete
                    options={fatherOccupationOptions.map(v => ({ value: v }))}
                    placeholder="e.g. Farmer, Doctor, Business..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item name="mother_name" label="Mother's Name"><Input placeholder="Enter mother's name" /></Form.Item>

                <Form.Item name="mother_occupation" label="Mother's Occupation">
                  <AutoComplete options={occupationOptions.map(v => ({ value: v }))} placeholder="e.g. Housewife, Teacher..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
              <div className="su-number-grid" style={{ marginTop: 12 }}>
                <Form.Item name="brother" label="Brothers" initialValue={0}>
                  <Input type="number" min={0} max={20} onChange={e => {
                    const t = parseInt(e.target.value) || 0;
                    const c = parseInt(form.getFieldValue("brother_marrige")) || 0;
                    if (c > t) form.setFieldsValue({ brother_marrige: t });
                    form.validateFields(["brother_marrige"]);
                  }} />
                </Form.Item>
                <Form.Item name="brother_marrige" label="Married Bros" initialValue={0} dependencies={["brother"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = parseInt(value) || 0; const total = parseInt(getFieldValue("brother")) || 0;
                      if (married < 0) return Promise.reject("0 se kam nahi");
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    }
                  })]}>
                  <Input type="number" min={0} onChange={() => form.validateFields(["brother_marrige"])} />
                </Form.Item>
                <Form.Item name="sister" label="Sisters" initialValue={0}>
                  <Input type="number" min={0} max={20} onChange={e => {
                    const t = parseInt(e.target.value) || 0;
                    const c = parseInt(form.getFieldValue("sister_marrige")) || 0;
                    if (c > t) form.setFieldsValue({ sister_marrige: t });
                    form.validateFields(["sister_marrige"]);
                  }} />
                </Form.Item>
                <Form.Item name="sister_marrige" label="Married Sis" initialValue={0} dependencies={["sister"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = parseInt(value) || 0; const total = parseInt(getFieldValue("sister")) || 0;
                      if (married < 0) return Promise.reject("0 se kam nahi");
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    }
                  })]}>
                  <Input type="number" min={0} onChange={() => form.validateFields(["sister_marrige"])} />
                </Form.Item>
              </div>
            </div>

            {/* Education */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji education">🎓</div>
                <div><div className="su-section-title">Education & Career</div><div className="su-section-subtitle">Qualifications & profession</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="university" label="University / College"><Input placeholder="Enter university name" /></Form.Item>
                <Form.Item name="course" label="Highest Qualification">
                  <AutoComplete options={courseOptions.map(v => ({ value: v }))} placeholder="e.g. B.Tech, MBA, 12th..." allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => { if (subCourseData[value]) handleCourseChange(value); else { setIsOtherCourse(true); setSubCourses([]); } }} />
                </Form.Item>
                <Form.Item name="sub_course" label="Specialization">
                  <AutoComplete options={subCourses.map(v => ({ value: v }))} placeholder="e.g. Computer Science, Finance..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="job_title" label="Job / Profession"><Input placeholder="e.g. Software Engineer" /></Form.Item>
                <Form.Item name="job_type" label="Employment Type">
                  <AutoComplete options={["Government", "Private", "Self Employed", "Business", "Not Working", "Retired", "Student"].map(v => ({ value: v }))} placeholder="e.g. Government, Private..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="annual_income" label="Annual Income (₹)"><Input type="number" placeholder="e.g. 500000" /></Form.Item>
                <Form.Item name="Lifestyle" label="Lifestyle">
                  <AutoComplete options={["Simple", "Moderate", "Rich", "Luxury", "Middle Class", "Upper Middle Class"].map(v => ({ value: v }))} placeholder="e.g. Simple, Moderate..." allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="su-form-footer">
            <div className="su-form-footer-left"><SafetyOutlined /><span>All data is securely stored</span></div>
            <div className="su-form-footer-actions">
              <Button onClick={handleModalClose} className="su-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading} icon={<HeartFilled />} className="su-save-btn">Save User Profile</Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* EDIT USER MODAL */}
      <Modal
        title={
          <div className="su-modal-title-new">
            <div className="su-modal-title-icon-wrap"><EditOutlined /></div>
            <div className="su-modal-title-text">
              <h3>Edit User Profile</h3>
              <p>Update user information</p>
            </div>
          </div>
        }
        open={editModalOpen}
        onCancel={handleEditModalClose}
        footer={null}
        width={880}
        destroyOnHidden
        className="su-add-modal"
        centered
      >
        <div className="su-staff-strip">
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon staff"><IdcardOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Profile ID</div>
              <div className="su-staff-badge-value">{selectedUser?.profile_id || generateRefNumber("user", selectedUser?.id)}</div>
            </div>
          </div>
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon referral"><UserOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Editing</div>
              <div className="su-staff-badge-value">{selectedUser?.first_name || selectedUser?.username}</div>
            </div>
          </div>
        </div>

        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
          requiredMark={false}
          onFinishFailed={errorInfo => {
            if (errorInfo.errorFields.length > 0) {
              message.error(`Please fix ${errorInfo.errorFields.length} error(s)`);
              editForm.scrollToField(errorInfo.errorFields[0].name, { behavior: "smooth", block: "center" });
            }
          }}
        >
          <div className="su-photo-section">
            <Upload beforeUpload={f => { setFile(f); setPreviewUrl(URL.createObjectURL(f)); return false; }} showUploadList={false}>
              <div className="su-photo-upload-new">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="su-photo-preview-new" />
                ) : selectedUser?.pic ? (
                  <img src={selectedUser.pic} alt="user" className="su-photo-preview-new" />
                ) : (
                  <div className="su-photo-placeholder"><CameraOutlined /><span>Update Photo</span></div>
                )}
              </div>
            </Upload>
            <p className="su-photo-hint-new">📸 Click to change photo</p>
          </div>

          <div className="su-form-body">

            {/* Account + Password */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji account">🔐</div>
                <div><div className="su-section-title">Account Information</div><div className="su-section-subtitle">User login credentials</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="username" label={<span><UserOutlined style={{ marginRight: 6, color: "#e91e63" }} />Username</span>}>
                  <Input placeholder="Username" disabled />
                </Form.Item>
                <Form.Item name="contact" label={<span><PhoneOutlined style={{ marginRight: 6, color: "#e91e63" }} />Mobile <span className="su-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Mobile is required" },
                    { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }
                  ]}
                  normalize={value => value?.replace(/\D/g, '').slice(0, 10)}>
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>
                <Form.Item name="email" label={<span><MailOutlined style={{ marginRight: 6, color: "#e91e63" }} />Email <span className="su-required-star">*</span></span>}
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Invalid email format" }
                  ]}>
                  <Input placeholder="user@email.com" />
                </Form.Item>

                {/* Password Toggle */}
                <Form.Item label=" " colon={false}>
                  <div
                    onClick={() => {
                      setShowPasswordChange(!showPasswordChange);
                      if (showPasswordChange) editForm.setFieldsValue({ new_password: '', confirm_password: '' });
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px',
                      background: showPasswordChange ? '#fff1f5' : '#f8fafc',
                      border: `2px dashed ${showPasswordChange ? '#ff0055' : '#cbd5e1'}`,
                      borderRadius: 10, cursor: 'pointer', transition: 'all 0.25s ease', userSelect: 'none',
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: showPasswordChange ? '#ff0055' : '#e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s',
                    }}>
                      <LockOutlined style={{ color: showPasswordChange ? '#fff' : '#64748b', fontSize: 14 }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: showPasswordChange ? '#ff0055' : '#374151' }}>
                        {showPasswordChange ? '🔒 Cancel Password Change' : '🔑 Change Password'}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        {showPasswordChange ? 'Click to cancel' : 'Click to set new password'}
                      </div>
                    </div>
                    {showPasswordChange && <Tag color="error" style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700 }}>Active</Tag>}
                  </div>
                </Form.Item>
              </div>

              {/* Password Fields */}
              {showPasswordChange && (
                <div style={{ background: 'linear-gradient(135deg, #fff1f5, #fce7f3)', border: '2px solid #fecdd3', borderRadius: 14, padding: '18px 18px 10px', marginTop: 4, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #fecdd3' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #ff0055, #ff4d88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LockOutlined style={{ color: '#fff', fontSize: 16 }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#be123c' }}>Set New Password</div>
                      <div style={{ fontSize: 11, color: '#fb7185' }}>This will immediately update the user's login password</div>
                    </div>
                    <Tag color="error" style={{ marginLeft: 'auto', fontWeight: 700 }}>Admin Only</Tag>
                  </div>

                  <div className="su-form-grid-new">
                    <Form.Item name="new_password"
                      label={<span style={{ color: '#be123c', fontWeight: 600 }}><LockOutlined style={{ marginRight: 6 }} />New Password <span className="su-required-star">*</span></span>}
                      rules={[
                        { required: showPasswordChange, message: "New password required" },
                        { min: 8, message: "Min 8 characters" },
                        { pattern: /[A-Z]/, message: "At least one uppercase letter" },
                        { pattern: /[0-9]/, message: "At least one number" },
                      ]}>
                      <Input.Password placeholder="Enter new password" autoComplete="new-password" size="large"
                        style={{ borderColor: '#fda4af', borderRadius: 8 }}
                        iconRender={visible => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />} />
                    </Form.Item>

                    <Form.Item name="confirm_password"
                      label={<span style={{ color: '#be123c', fontWeight: 600 }}><SafetyOutlined style={{ marginRight: 6 }} />Confirm Password <span className="su-required-star">*</span></span>}
                      dependencies={['new_password']}
                      rules={[
                        { required: showPasswordChange, message: "Please confirm password" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                            return Promise.reject(new Error('Passwords do not match!'));
                          },
                        }),
                      ]}>
                      <Input.Password placeholder="Re-enter new password" size="large"
                        style={{ borderColor: '#fda4af', borderRadius: 8 }}
                        iconRender={visible => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />} />
                    </Form.Item>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 14px', background: '#fff', borderRadius: 8, border: '1px solid #fecdd3', marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>⚠️ Requirements:</span>
                    {['Min 8 characters', '1 Uppercase letter', '1 Number'].map(req => (
                      <Tag key={req} style={{ fontSize: 10, background: '#fff1f5', color: '#be123c', border: '1px solid #fecdd3' }}>✓ {req}</Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Personal */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji personal">👤</div>
                <div><div className="su-section-title">Personal Details</div><div className="su-section-subtitle">Basic profile information</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="firstname" label={<span>Full Name <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Full Name is required" }]}>
                  <Input placeholder="Enter full name" />
                </Form.Item>
                <Form.Item name="gender" label={<span>Gender <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Gender is required" }]}>
                  <AutoComplete options={["Male", "Female", "Other", "Prefer not to say"].map(v => ({ value: v }))} placeholder="Select or type gender" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="dob" label={<span>Date of Birth <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Date of Birth is required" }]}>
                  <Input type="date" />
                </Form.Item>
                <Form.Item name="marrige_status" label={<span>Marital Status <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Marital Status is required" }]}>
                  <AutoComplete options={["Unmarried", "Divorced", "Widowed", "Separated", "Awaited"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="height" label="Height">
                  <AutoComplete options={heightOptions} placeholder="e.g. 5'6&quot; (168 cm)" allowClear
                    filterOption={(input, option) => {
                      const s = input.toLowerCase();
                      return (option?.value || '').toLowerCase().includes(s) || (option?.label || '').toLowerCase().includes(s);
                    }} />
                </Form.Item>
                <Form.Item name="blood_group" label="Blood Group">
                  <AutoComplete options={bloodGroupOptions.map(v => ({ value: v }))} placeholder="e.g. A+" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="mother_tongue" label="Mother Tongue">
                  <AutoComplete options={motherTongueOptions.map(v => ({ value: v }))} placeholder="Select language" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="food_lifestyle" label="Food Lifestyle">
                  <AutoComplete options={["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan", "Jain"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
            </div>

            {/* Location */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji location">📍</div>
                <div><div className="su-section-title">Location Details</div><div className="su-section-subtitle">Where does the user live?</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="state" label={<span>State <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "State is required" }]}>
                  <AutoComplete options={states.map(s => ({ value: s.state || s }))} placeholder="Type or select state" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => {
                      const m = states.find(s => (s.state || s) === value);
                      if (m) { editForm.setFieldsValue({ district: undefined, city: undefined }); handleStateChange(value); }
                    }} />
                </Form.Item>
                <Form.Item name="district" label={<span>District <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "District is required" }]}>
                  <AutoComplete options={districts.map(d => ({ value: d }))} placeholder="Type or select district" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => { const m = districts.find(d => d === value); if (m) handleDistrictChange(value); }} />
                </Form.Item>
                <Form.Item name="city" label={<span>City / Town <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "City is required" }]}>
                  <AutoComplete options={cities.map(c => ({ value: c }))} placeholder="Enter city name" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="country" label="Country"><Input /></Form.Item>
                <Form.Item name="contact1" label="Alternate Mobile"
                  rules={[{ pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }]}
                  normalize={value => value?.replace(/\D/g, '').slice(0, 10)}>
                  <Input placeholder="Optional" maxLength={10} inputMode="numeric" />
                </Form.Item>
              </div>
            </div>

            {/* Religion */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji religion">🕉️</div>
                <div><div className="su-section-title">Religion & Caste</div><div className="su-section-subtitle">Religious background</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="religion" label={<span>Religion <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Religion is required" }]}>
                  <AutoComplete
                    options={religionOptions.map(r => ({ value: r.value || r, label: `${getReligionIcon(r.value || r)} ${r.label || r.value || r}` }))}
                    placeholder="Select religion" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => {
                      const casteReligions = ['Hindu', 'Muslim', 'MuslimShia', 'MuslimSunni', 'Christian', 'Sikh', 'Jain', 'JainDigambar', 'JainShwetambar', 'Buddhist', 'Parsi', 'Jewish', 'InterReligion', 'NoReligiousBelief'];
                      if (casteReligions.includes(value)) { editForm.setFieldsValue({ caste: undefined }); handleReligionChange(value); }
                      else { setFilteredCastes([]); editForm.setFieldsValue({ caste: undefined }); }
                    }}
                  />
                </Form.Item>
                <Form.Item name="caste" label={<span>Caste <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Caste is required" }]}>
                  <AutoComplete options={filteredCastes.map(c => ({ value: c.name || c }))} placeholder="Select or type caste" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="subcaste" label={<span>Sub Caste <span className="su-required-star">*</span></span>}
                  rules={[{ required: true, message: "Sub Caste is required" }]}>
                  <Input placeholder="Type sub caste" />
                </Form.Item>
              </div>
            </div>

            {/* Family */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji family">👨‍👩‍👧‍👦</div>
                <div><div className="su-section-title">Family Details</div><div className="su-section-subtitle">Family background information</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="father_name" label="Father's Name"><Input placeholder="Enter father's name" /></Form.Item>
                <Form.Item name="mother_name" label="Mother's Name"><Input placeholder="Enter mother's name" /></Form.Item>
                <Form.Item name="father_occupation" label="Father's Occupation">
                  <AutoComplete options={occupationOptions.map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="mother_occupation" label="Mother's Occupation">
                  <AutoComplete options={occupationOptions.map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
              <div className="su-number-grid" style={{ marginTop: 12 }}>
                <Form.Item name="brother" label="Brothers">
                  <Input type="number" min={0} max={20} onChange={e => {
                    const t = parseInt(e.target.value) || 0;
                    const c = parseInt(editForm.getFieldValue("brother_marrige")) || 0;
                    if (c > t) editForm.setFieldsValue({ brother_marrige: t });
                    editForm.validateFields(["brother_marrige"]);
                  }} />
                </Form.Item>
                <Form.Item name="brother_marrige" label="Married Bros" dependencies={["brother"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = parseInt(value) || 0; const total = parseInt(getFieldValue("brother")) || 0;
                      if (married < 0) return Promise.reject("0 se kam nahi");
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    }
                  })]}>
                  <Input type="number" min={0} onChange={() => editForm.validateFields(["brother_marrige"])} />
                </Form.Item>
                <Form.Item name="sister" label="Sisters">
                  <Input type="number" min={0} max={20} onChange={e => {
                    const t = parseInt(e.target.value) || 0;
                    const c = parseInt(editForm.getFieldValue("sister_marrige")) || 0;
                    if (c > t) editForm.setFieldsValue({ sister_marrige: t });
                    editForm.validateFields(["sister_marrige"]);
                  }} />
                </Form.Item>
                <Form.Item name="sister_marrige" label="Married Sis" dependencies={["sister"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = parseInt(value) || 0; const total = parseInt(getFieldValue("sister")) || 0;
                      if (married < 0) return Promise.reject("0 se kam nahi");
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    }
                  })]}>
                  <Input type="number" min={0} onChange={() => editForm.validateFields(["sister_marrige"])} />
                </Form.Item>
              </div>
            </div>

            {/* Education */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji education">🎓</div>
                <div><div className="su-section-title">Education & Career</div><div className="su-section-subtitle">Qualifications & profession</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="university" label="University / College"><Input placeholder="Enter university name" /></Form.Item>
                <Form.Item name="course" label="Highest Qualification">
                  <AutoComplete options={courseOptions.map(v => ({ value: v }))} placeholder="Select course" allowClear
                    filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())}
                    onChange={value => { if (subCourseData[value]) { editForm.setFieldsValue({ sub_course: undefined }); handleCourseChange(value); } }} />
                </Form.Item>
                <Form.Item name="sub_course" label="Specialization">
                  <AutoComplete options={subCourses.map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="job_title" label="Job / Profession"><Input placeholder="e.g. Software Engineer" /></Form.Item>
                <Form.Item name="job_type" label="Employment Type">
                  <AutoComplete options={["Government", "Private", "Self Employed", "Business", "Not Working", "Retired", "Student"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item name="annual_income" label="Annual Income (₹)"><Input type="number" placeholder="e.g. 500000" /></Form.Item>
                <Form.Item name="Lifestyle" label="Lifestyle">
                  <AutoComplete options={["Simple", "Moderate", "Rich", "Luxury", "Middle Class", "Upper Middle Class"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
            </div>

            {/* Additional */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji additional">📝</div>
                <div><div className="su-section-title">Additional Information</div><div className="su-section-subtitle">Optional details</div></div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item name="created_profile" label="Profile Created By">
                  <AutoComplete options={["Self", "Parent", "Sibling", "Friend", "Staff", "Admin", "Relative", "Other"].map(v => ({ value: v }))} placeholder="Select or type" allowClear filterOption={(input, option) => option?.value?.toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>
              <div className="su-about-box" style={{ marginTop: 12 }}>
                <div className="su-about-box-header">
                  <span className="su-about-box-label">📝 About the User</span>
                  <span className="su-about-box-badge">Optional</span>
                </div>
                <Form.Item name="additional_notes" style={{ marginBottom: 0 }}>
                  <Input.TextArea maxLength={500} autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Write something about this user..." style={{ border: "none", boxShadow: "none", padding: 0, resize: "none" }} />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="su-form-footer">
            <div className="su-form-footer-left"><SafetyOutlined /><span>All changes are securely saved</span></div>
            <div className="su-form-footer-actions">
              <Button onClick={handleEditModalClose} className="su-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading} icon={<EditOutlined />} className="su-save-btn">Update Profile</Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* DETAILS DRAWER */}
      <Drawer
        title={
          <div className="su-view-drawer-title">
            <div className="su-view-title-icon"><UserOutlined /></div>
            <div><h3>Profile Details</h3><p>Complete Information</p></div>
          </div>
        }
        placement="right"
        width={520}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
        className="su-view-drawer"
      >
        {selectedUser && (
          <div className="su-view-content">
            <div className="su-view-header">
              <div className="su-view-avatar">
                {selectedUser.pic
                  ? <img src={selectedUser.pic} alt="profile" />
                  : <span>{selectedUser.first_name?.[0] || selectedUser.username?.[0] || "U"}</span>
                }
              </div>
              <h2 className="su-view-name">{capitalize(selectedUser.first_name) || selectedUser.username || "N/A"}</h2>
              <p className="su-view-ref">{selectedUser.profile_id || generateRefNumber("user", selectedUser.id)}</p>

              {(() => {
                const method = methodConfig[selectedUser._detected_method] || methodConfig.self;
                const staffData = staffList[selectedUser._detected_staff];
                let displayText = "Self Registered";
                let displayIcon = "🌐";
                let bgColor = "#f0fdf4";
                let textColor = "#16a34a";
                let borderColor = "#bbf7d0";
                if (selectedUser._detected_method !== "self") {
                  if (staffData) {
                    displayText = `${staffData.name} • via ${method.label}`;
                    displayIcon = method.icon;
                    bgColor = method.bg;
                    textColor = method.color;
                    borderColor = method.border;
                  } else if (selectedUser.refer_by) {
                    displayText = `Referral Code: ${selectedUser.refer_by}`;
                    displayIcon = "🎁";
                    bgColor = "#fff7ed";
                    textColor = "#ea580c";
                    borderColor = "#fed7aa";
                  }
                }
                return (
                  <div className="su-view-source" style={{ background: bgColor, color: textColor, border: `1.5px solid ${borderColor}` }}>
                    {displayIcon} {displayText}
                  </div>
                );
              })()}

              <div className="su-view-tags">
                <Tag className={`status-tag ${selectedUser.is_active ? "active" : "inactive"}`}>
                  {selectedUser.is_active ? "● Active" : "○ Inactive"}
                </Tag>
                <Tag className={`plan-tag ${selectedUser.is_premium ? "premium" : "free"}`}>
                  {selectedUser.is_premium ? "⭐ Premium" : "Free"}
                </Tag>
              </div>

              <div className="su-view-actions">
                {selectedUser.contact && (
                  <>
                    <Button icon={<PhoneOutlined />} className="su-view-btn call" onClick={() => handleCall(selectedUser.contact)}>Call</Button>
                    <Button icon={<WhatsAppOutlined />} className="su-view-btn whatsapp" onClick={() => window.open(`https://wa.me/${selectedUser.contact.replace(/\D/g, "")}`)}>WhatsApp</Button>
                  </>
                )}
                {selectedUser.email && (
                  <Button icon={<MailOutlined />} className="su-view-btn email" onClick={() => window.location.href = `mailto:${selectedUser.email}`}>Email</Button>
                )}
              </div>
            </div>

            {/* Plan & Payment */}
            {(selectedUser.is_premium || selectedUser.total_spent > 0) && (
              <div className="su-view-section" style={{ background: 'linear-gradient(135deg, #fff7ed, #fef3c7)', border: '2px solid #fde68a', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <div className="su-view-section-header">
                  <div className="su-section-emoji" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>💎</div>
                  <h4>Plan & Payment Info</h4>
                </div>
                <div className="su-view-grid">
                  {selectedUser.plan_name && (
                    <div className="su-view-item"><span className="su-view-label">Plan Name</span><span className="su-view-value" style={{ color: '#ff0055', fontWeight: 700 }}>⭐ {selectedUser.plan_name}</span></div>
                  )}
                  {selectedUser.plan_amount > 0 ? (
                    <div className="su-view-item"><span className="su-view-label">Plan Amount</span><span className="su-view-value" style={{ color: '#10b981', fontWeight: 700 }}>₹{parseFloat(selectedUser.plan_amount).toLocaleString('en-IN')}</span></div>
                  ) : selectedUser.is_premium ? (
                    <div className="su-view-item"><span className="su-view-label">Plan Amount</span><span className="su-view-value" style={{ color: '#ec4899', fontWeight: 700 }}>🎁 FREE</span></div>
                  ) : null}
                  {selectedUser.payment_mode && (
                    <div className="su-view-item">
                      <span className="su-view-label">Payment Mode</span>
                      <span className="su-view-value" style={{ fontWeight: 600 }}>
                        {selectedUser.payment_mode === 'cash' && '💵 Cash Received'}
                        {selectedUser.payment_mode === 'online' && '💳 Online Payment'}
                        {selectedUser.payment_mode === 'upi' && '📱 UPI Transfer'}
                        {selectedUser.payment_mode === 'bank_transfer' && '🏦 Bank Transfer'}
                        {selectedUser.payment_mode === 'gift' && '🎁 Free / Gift'}
                      </span>
                    </div>
                  )}
                  {selectedUser.plan_start_date && (
                    <div className="su-view-item"><span className="su-view-label">Plan Started</span><span className="su-view-value">{formatDate(selectedUser.plan_start_date)}</span></div>
                  )}
                  {selectedUser.plan_expiry && (
                    <div className="su-view-item">
                      <span className="su-view-label">Plan Expires</span>
                      <span className="su-view-value" style={{ color: new Date(selectedUser.plan_expiry) < new Date() ? '#dc2626' : '#10b981', fontWeight: 700 }}>
                        {formatDate(selectedUser.plan_expiry)}
                        {new Date(selectedUser.plan_expiry) < new Date() && ' (Expired)'}
                      </span>
                    </div>
                  )}
                  {selectedUser.granted_by_name && (
                    <div className="su-view-item"><span className="su-view-label">Granted By</span><span className="su-view-value" style={{ color: '#7c3aed', fontWeight: 700 }}>👑 {selectedUser.granted_by_name}</span></div>
                  )}
                  {selectedUser.total_spent > 0 && (
                    <div className="su-view-item"><span className="su-view-label">Total Spent</span><span className="su-view-value" style={{ color: '#059669', fontWeight: 700 }}>₹{parseFloat(selectedUser.total_spent).toLocaleString('en-IN')}</span></div>
                  )}
                  {selectedUser.transaction_count > 0 && (
                    <div className="su-view-item"><span className="su-view-label">Total Purchases</span><span className="su-view-value" style={{ fontWeight: 600 }}>{selectedUser.transaction_count} time(s)</span></div>
                  )}
                </div>
              </div>
            )}

            {/* Account Info */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji account">🔐</div><h4>Account Information</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">Username</span>  <span className="su-view-value">{selectedUser.username || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Profile ID</span><span className="su-view-value mono">{selectedUser.profile_id || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Email</span>     <span className="su-view-value">{selectedUser.email || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Mobile</span>    <span className="su-view-value">{selectedUser.contact || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Alt. Mobile</span><span className="su-view-value">{selectedUser.contact1 || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Joined</span>    <span className="su-view-value">{formatDate(selectedUser.created_date || selectedUser.date_joined)}</span></div>
              </div>
            </div>

            {/* Personal */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji personal">👤</div><h4>Personal Details</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">Full Name</span>     <span className="su-view-value">{capitalize(selectedUser.first_name) || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Gender</span>        <span className="su-view-value">{capitalize(selectedUser.gender) || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">DOB</span>           <span className="su-view-value">{selectedUser.dob || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Age</span>           <span className="su-view-value">{selectedUser.age || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Marital Status</span><span className="su-view-value">{selectedUser.marrige_status || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Height</span>        <span className="su-view-value">{selectedUser.height || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Blood Group</span>   <span className="su-view-value">{selectedUser.blood_group || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Mother Tongue</span> <span className="su-view-value">{selectedUser.mother_tongue || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Food</span>          <span className="su-view-value">{selectedUser.food_lifestyle || "N/A"}</span></div>
              </div>
            </div>

            {/* Location */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji location">📍</div><h4>Location Details</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">State</span>   <span className="su-view-value">{selectedUser.state || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">District</span><span className="su-view-value">{selectedUser.district || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">City</span>    <span className="su-view-value">{selectedUser.city || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Country</span> <span className="su-view-value">{selectedUser.country || "India"}</span></div>
              </div>
            </div>

            {/* Religion */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji religion">🕉️</div><h4>Religion & Caste</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">Religion</span> <span className="su-view-value">{selectedUser.religion || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Caste</span>    <span className="su-view-value">{selectedUser.caste || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Sub Caste</span><span className="su-view-value">{selectedUser.subcaste || "N/A"}</span></div>
              </div>
            </div>

            {/* Family */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji family">👨‍👩‍👧‍👦</div><h4>Family Details</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">Father</span>      <span className="su-view-value">{selectedUser.father_name || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Mother</span>      <span className="su-view-value">{selectedUser.mother_name || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Father Occ.</span> <span className="su-view-value">{selectedUser.father_occupation || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Mother Occ.</span> <span className="su-view-value">{selectedUser.mother_occupation || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Brothers</span>    <span className="su-view-value">{selectedUser.brother || 0} ({selectedUser.brother_marrige || 0} married)</span></div>
                <div className="su-view-item"><span className="su-view-label">Sisters</span>     <span className="su-view-value">{selectedUser.sister || 0} ({selectedUser.sister_marrige || 0} married)</span></div>
              </div>
            </div>

            {/* Education */}
            <div className="su-view-section">
              <div className="su-view-section-header"><div className="su-section-emoji education">🎓</div><h4>Education & Career</h4></div>
              <div className="su-view-grid">
                <div className="su-view-item"><span className="su-view-label">University</span>    <span className="su-view-value">{selectedUser.university || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Course</span>        <span className="su-view-value">{selectedUser.course || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Specialization</span><span className="su-view-value">{selectedUser.sub_course || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Job Title</span>     <span className="su-view-value">{selectedUser.job_title || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Job Type</span>      <span className="su-view-value">{selectedUser.job_type || "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Income</span>        <span className="su-view-value">{selectedUser.annual_income ? formatCurrency(selectedUser.annual_income) : "N/A"}</span></div>
                <div className="su-view-item"><span className="su-view-label">Lifestyle</span>     <span className="su-view-value">{selectedUser.Lifestyle || "N/A"}</span></div>
              </div>
            </div>

            {/* Additional */}
            {(selectedUser.created_profile || selectedUser.additional_notes) && (
              <div className="su-view-section">
                <div className="su-view-section-header"><div className="su-section-emoji additional">📝</div><h4>Additional Info</h4></div>
                <div className="su-view-grid">
                  <div className="su-view-item"><span className="su-view-label">Created By</span><span className="su-view-value">{selectedUser.created_profile || "N/A"}</span></div>
                </div>
                {selectedUser.additional_notes && (
                  <div className="su-view-notes">
                    <div className="su-view-notes-label">📝 About:</div>
                    <p>{selectedUser.additional_notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="su-view-bottom-actions">
              <Button icon={<EditOutlined />} className="su-view-edit-btn" onClick={() => { setDetailsDrawerOpen(false); handleEdit(selectedUser); }}>Edit Profile</Button>
              <Button danger icon={<DeleteOutlined />} className="su-view-delete-btn" onClick={() => { setDetailsDrawerOpen(false); handleDelete(selectedUser.id); }}>Delete</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default User_Tables;